import Close from "@mui/icons-material/Close";
import WarningAmber from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type SubmitEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@/app/store/hooks";
import { updateChatTitle } from "@/features/chat/model/chatHistorySlice";
import type { ChatSessionSummary } from "@/features/chat/model/chatHistoryTypes";
import { getChatSessionDisplayTitle } from "@/features/chat/model/filterChatSessions";

type EditChatTitleModalProps = {
  open: boolean;
  session: ChatSessionSummary | null;
  onClose: () => void;
};

export function EditChatTitleModal({ open, session, onClose }: EditChatTitleModalProps) {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !session) {
      setTitle("");
      setError(null);
      setValidationError(null);
      setIsSaving(false);
      return;
    }

    const displayTitle = getChatSessionDisplayTitle(session, t("history.untitled"));
    setTitle(session.title?.trim() ? displayTitle : "");
    setError(null);
    setValidationError(null);
    setIsSaving(false);
  }, [open, session, t]);

  useEffect(() => {
    if (!open || !session) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      titleInputRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [open, session]);

  function handleDialogClose(_event: object, reason?: "backdropClick" | "escapeKeyDown") {
    if (reason === "backdropClick" || isSaving) {
      return;
    }
    onClose();
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session) {
      return;
    }

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError(t("history.editTitleModal.validation.required"));
      return;
    }

    setValidationError(null);
    setError(null);
    setIsSaving(true);

    try {
      await dispatch(
        updateChatTitle({
          sessionId: session.sessionId,
          title: trimmedTitle,
        }),
      ).unwrap();
      onClose();
    } catch (saveError) {
      const message =
        typeof saveError === "string" ? saveError : t("history.editTitleModal.errors.saveFailed");
      setError(message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            bgcolor: "var(--theme-surface)",
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          background: "var(--theme-gradientBackground)",
          color: "var(--theme-text-on-primary)",
          "& .MuiIconButton-root": {
            color: "var(--theme-text-on-primary)",
            "&:hover": {
              bgcolor: "var(--theme-hover-secondary)",
            },
          },
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          {t("history.editTitleModal.title")}
        </Typography>
        <IconButton
          aria-label={t("history.editTitleModal.closeAria")}
          onClick={onClose}
          disabled={isSaving}
          edge="end"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            {error ? (
              <Alert severity="error" icon={<WarningAmber />}>
                {error}
              </Alert>
            ) : null}
            <TextField
              label={t("history.editTitleModal.label")}
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                if (validationError) {
                  setValidationError(null);
                }
              }}
              error={Boolean(validationError)}
              helperText={validationError ?? undefined}
              fullWidth
              inputRef={titleInputRef}
              disabled={isSaving}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isSaving} sx={{ textTransform: "none" }}>
            {t("history.editTitleModal.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: "none" }}
          >
            {t("history.editTitleModal.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
